/*
  PA Morse necklace preview
  - Fixed 45 cm front-view chain.
  - Dots (2 mm) and dashes (3 mm) follow the chain without changing its shape.
  - The active Shopify variant controls the metal finish.
*/
(function () {
  'use strict';

  if (window.PAMorseNecklacePreviewLoaded) return;
  window.PAMorseNecklacePreviewLoaded = true;

  var MORSE = {
    A: '.-', B: '-...', C: '-.-.', D: '-..', E: '.', F: '..-.',
    G: '--.', H: '....', I: '..', J: '.---', K: '-.-', L: '.-..',
    M: '--', N: '-.', O: '---', P: '.--.', Q: '--.-', R: '.-.',
    S: '...', T: '-', U: '..-', V: '...-', W: '.--', X: '-..-',
    Y: '-.--', Z: '--..',
    0: '-----', 1: '.----', 2: '..---', 3: '...--', 4: '....-',
    5: '.....', 6: '-....', 7: '--...', 8: '---..', 9: '----.'
  };

  var FINISHES = {
    gold: {
      label: 'Dourado',
      dark: '#bc8626',
      mid: '#e2b94f',
      light: '#fff2b6',
      chain: '#d5a541',
      filter: 'none'
    },
    silver: {
      label: 'Prateado',
      dark: '#899295',
      mid: '#c8ced0',
      light: '#ffffff',
      chain: '#aeb5b7',
      filter: 'grayscale(1) saturate(.25) brightness(1.13) contrast(.9)'
    },
    rose: {
      label: 'Ouro rosa',
      dark: '#ac7463',
      mid: '#dfaa94',
      light: '#f9d9ca',
      chain: '#c58c78',
      filter: 'sepia(.18) saturate(.76) hue-rotate(322deg) brightness(1.03)'
    }
  };

  var CANVAS_WIDTH = 760;
  var SOURCE_HEIGHT = 820;
  var CANVAS_HEIGHT = 650;
  var DOT_DIAMETER = 25;
  var DASH_WIDTH = 37;
  var PIECE_HEIGHT = 24;
  var PIECE_GAP = 3;

  function normalizeCharacter(character) {
    return String(character || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toUpperCase();
  }

  function normalizeName(value) {
    return Array.from(String(value || '')).reduce(function (result, character) {
      var normalized = normalizeCharacter(character);
      if (normalized.length === 1 && MORSE[normalized] && result.length < 10) {
        return result + normalized;
      }
      return result;
    }, '');
  }

  function limitInput(value) {
    var eligible = 0;
    var result = '';

    Array.from(String(value || '')).forEach(function (character) {
      var normalized = normalizeCharacter(character);

      if (normalized.length === 1 && MORSE[normalized]) {
        if (eligible >= 10) return;
        eligible += 1;
        result += character;
        return;
      }

      if (/^[\s'-]$/.test(character)) {
        result += character;
      }
    });

    return result.slice(0, 24);
  }

  function cubicPoint(start, controlA, controlB, end, t) {
    var inverse = 1 - t;
    return {
      x: Math.pow(inverse, 3) * start.x
        + 3 * Math.pow(inverse, 2) * t * controlA.x
        + 3 * inverse * Math.pow(t, 2) * controlB.x
        + Math.pow(t, 3) * end.x,
      y: Math.pow(inverse, 3) * start.y
        + 3 * Math.pow(inverse, 2) * t * controlA.y
        + 3 * inverse * Math.pow(t, 2) * controlB.y
        + Math.pow(t, 3) * end.y
    };
  }

  function createFixedPath() {
    var segments = [
      [
        { x: 268, y: -18 },
        { x: 250, y: 130 },
        { x: 142, y: 314 },
        { x: 128, y: 515 }
      ],
      [
        { x: 128, y: 515 },
        { x: 119, y: 645 },
        { x: 206, y: 730 },
        { x: 380, y: 758 }
      ],
      [
        { x: 380, y: 758 },
        { x: 554, y: 730 },
        { x: 641, y: 645 },
        { x: 632, y: 515 }
      ],
      [
        { x: 632, y: 515 },
        { x: 618, y: 314 },
        { x: 510, y: 130 },
        { x: 492, y: -18 }
      ]
    ];
    var points = [];

    segments.forEach(function (segment, segmentIndex) {
      var steps = 180;
      var step;
      for (step = segmentIndex === 0 ? 0 : 1; step <= steps; step += 1) {
        points.push(cubicPoint(segment[0], segment[1], segment[2], segment[3], step / steps));
      }
    });

    var distance = 0;
    var samples = points.map(function (point, index) {
      var previous;
      var next;
      if (index > 0) {
        distance += Math.hypot(point.x - points[index - 1].x, point.y - points[index - 1].y);
      }
      previous = points[Math.max(0, index - 1)];
      next = points[Math.min(points.length - 1, index + 1)];
      return {
        x: point.x,
        y: point.y,
        distance: distance,
        angle: Math.atan2(next.y - previous.y, next.x - previous.x)
      };
    });

    return { samples: samples, length: distance };
  }

  var FIXED_PATH = createFixedPath();

  function pointAtDistance(targetDistance) {
    var target = Math.max(0, Math.min(FIXED_PATH.length, targetDistance));
    var low = 0;
    var high = FIXED_PATH.samples.length - 1;
    var middle;
    var next;
    var previous;
    var segmentLength;
    var progress;

    while (low < high) {
      middle = Math.floor((low + high) / 2);
      if (FIXED_PATH.samples[middle].distance < target) low = middle + 1;
      else high = middle;
    }

    next = FIXED_PATH.samples[low];
    previous = FIXED_PATH.samples[Math.max(0, low - 1)];
    segmentLength = next.distance - previous.distance;
    progress = segmentLength ? (target - previous.distance) / segmentLength : 0;

    return {
      x: previous.x + (next.x - previous.x) * progress,
      y: previous.y + (next.y - previous.y) * progress,
      angle: previous.angle + (next.angle - previous.angle) * progress
    };
  }

  function getSequenceLayout(sequence) {
    var widths = Array.from(sequence).map(function (symbol) {
      return symbol === '.' ? DOT_DIAMETER : DASH_WIDTH;
    });
    var span = widths.reduce(function (total, width) {
      return total + width;
    }, 0) + Math.max(0, widths.length - 1) * PIECE_GAP;

    return {
      widths: widths,
      span: span,
      startDistance: (FIXED_PATH.length - span) / 2
    };
  }

  function drawChain(context, finish) {
    var palette = FINISHES[finish];

    context.save();
    context.beginPath();
    FIXED_PATH.samples.forEach(function (point, index) {
      if (index === 0) context.moveTo(point.x, point.y);
      else context.lineTo(point.x, point.y);
    });
    context.strokeStyle = palette.dark;
    context.globalAlpha = 0.36;
    context.lineWidth = 1.65;
    context.stroke();
    context.globalAlpha = 0.9;
    context.strokeStyle = palette.chain;
    context.lineWidth = 1.05;
    context.lineCap = 'round';
    context.setLineDash([1.5, 1.6]);
    context.stroke();
    context.setLineDash([]);
    context.globalAlpha = 0.7;
    context.strokeStyle = palette.light;
    context.lineWidth = 0.42;
    context.translate(-0.34, -0.28);
    context.stroke();
    context.restore();
  }

  function drawFallbackPiece(context, symbol, width, finish) {
    var palette = FINISHES[finish];
    var gradient;

    context.shadowColor = 'rgba(70, 48, 13, .2)';
    context.shadowBlur = 3.8;
    context.shadowOffsetY = 1.8;

    if (symbol === '.') {
      gradient = context.createRadialGradient(-3.5, -4.5, 0.8, 0, 0, DOT_DIAMETER / 2);
      gradient.addColorStop(0, '#ffffff');
      gradient.addColorStop(0.22, palette.light);
      gradient.addColorStop(0.58, palette.mid);
      gradient.addColorStop(1, palette.dark);
      context.beginPath();
      context.arc(0, 0, DOT_DIAMETER / 2, 0, Math.PI * 2);
      context.fillStyle = gradient;
      context.fill();
      return;
    }

    gradient = context.createLinearGradient(-width / 2, -PIECE_HEIGHT / 2, width / 2, PIECE_HEIGHT / 2);
    gradient.addColorStop(0, palette.light);
    gradient.addColorStop(0.36, palette.mid);
    gradient.addColorStop(0.72, palette.dark);
    gradient.addColorStop(1, palette.light);
    context.beginPath();
    context.rect(-width / 2, -PIECE_HEIGHT / 2, width, PIECE_HEIGHT);
    context.fillStyle = gradient;
    context.fill();
  }

  function drawPiece(context, symbol, center, finish, images) {
    var width = symbol === '.' ? DOT_DIAMETER : DASH_WIDTH;
    var image = images ? (symbol === '.' ? images.dot : images.dash) : null;

    context.save();
    context.translate(center.x, center.y);
    context.rotate(center.angle);

    if (image && image.complete && image.naturalWidth) {
      context.shadowColor = 'rgba(70, 48, 13, .2)';
      context.shadowBlur = 3.8;
      context.shadowOffsetY = 1.8;
      context.filter = FINISHES[finish].filter;
      context.drawImage(
        image,
        0,
        0,
        image.naturalWidth,
        image.naturalHeight,
        -width / 2,
        -PIECE_HEIGHT / 2,
        width,
        PIECE_HEIGHT
      );
    } else {
      drawFallbackPiece(context, symbol, width, finish);
    }

    context.restore();
  }

  function drawLegend(context, letterCodes) {
    if (!letterCodes.length) return;

    var compact = letterCodes.length > 7;
    var boxWidth = Math.min(480, Math.max(178, letterCodes.length * 44 + 30));
    var boxX = (CANVAS_WIDTH - boxWidth) / 2;
    var cellWidth = (boxWidth - 28) / letterCodes.length;

    context.save();
    context.shadowColor = 'rgba(70, 68, 62, .04)';
    context.shadowBlur = 9;
    context.beginPath();
    context.moveTo(boxX - 8, 365);
    context.lineTo(boxX + 18, 348);
    context.lineTo(boxX + boxWidth - 22, 353);
    context.lineTo(boxX + boxWidth + 9, 368);
    context.lineTo(boxX + boxWidth - 5, 424);
    context.lineTo(boxX + 24, 429);
    context.lineTo(boxX - 10, 410);
    context.closePath();
    context.fillStyle = 'rgba(255,255,255,.76)';
    context.fill();
    context.shadowColor = 'transparent';

    context.beginPath();
    context.moveTo(boxX - 2, 380);
    context.lineTo(boxX + boxWidth + 4, 369);
    context.lineTo(boxX + boxWidth - 12, 418);
    context.lineTo(boxX + 12, 414);
    context.closePath();
    context.fillStyle = 'rgba(242,242,240,.44)';
    context.fill();

    letterCodes.forEach(function (item, index) {
      var x = boxX + 14 + cellWidth * (index + 0.5);
      context.textAlign = 'center';
      context.fillStyle = '#171715';
      context.font = '700 ' + (compact ? 16 : 21) + 'px Georgia, serif';
      context.fillText(item.letter, x, 381);
      context.fillStyle = '#292825';
      context.font = '800 ' + (compact ? 10 : 13) + 'px ui-monospace, SFMono-Regular, Consolas, monospace';
      context.fillText(item.code, x, 409);
    });

    context.strokeStyle = '#20201e';
    context.lineWidth = 2.6;
    context.lineCap = 'round';
    context.beginPath();
    context.moveTo(370, 448);
    context.bezierCurveTo(379, 477, 391, 523, 368, 548);
    context.bezierCurveTo(347, 570, 317, 559, 321, 538);
    context.bezierCurveTo(325, 516, 359, 516, 376, 539);
    context.bezierCurveTo(397, 567, 383, 595, 360, 614);
    context.stroke();
    context.beginPath();
    context.moveTo(360, 614);
    context.lineTo(360, 597);
    context.moveTo(360, 614);
    context.lineTo(377, 609);
    context.stroke();
    context.restore();
  }

  function drawPreview(canvas, sequence, finish, letterCodes, images) {
    var pixelRatio = Math.min(window.devicePixelRatio || 1, 2);
    var context;
    var layout;
    var cursor;
    var previewScale = CANVAS_HEIGHT / SOURCE_HEIGHT;

    canvas.width = CANVAS_WIDTH * pixelRatio;
    canvas.height = CANVAS_HEIGHT * pixelRatio;
    context = canvas.getContext('2d');
    if (!context) return;

    context.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
    context.clearRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);
    context.fillStyle = '#f5f5f4';
    context.fillRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);

    context.save();
    context.translate((CANVAS_WIDTH - CANVAS_WIDTH * previewScale) / 2, -30);
    context.scale(previewScale, previewScale);
    drawChain(context, finish);

    layout = getSequenceLayout(sequence);
    cursor = layout.startDistance;
    Array.from(sequence).forEach(function (symbol, index) {
      var width = layout.widths[index];
      drawPiece(context, symbol, pointAtDistance(cursor + width / 2), finish, images);
      cursor += width + PIECE_GAP;
    });
    context.restore();
  }

  function inferFinish(block) {
    var root = block.closest('[data-pa-personalization-root]');
    var text = root ? root.getAttribute('data-product-full-title') || '' : '';

    text = String(text)
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase();

    if (/rose|rosa/.test(text)) return 'rose';
    if (/dourad|gold|ouro/.test(text)) return 'gold';
    if (/prata|pratead|silver/.test(text)) return 'silver';
    return 'gold';
  }

  function loadImage(source, onReady) {
    var image = new Image();
    image.decoding = 'async';
    image.onload = onReady;
    image.onerror = onReady;
    image.src = source;
    return image;
  }

  function initializeBlock(block) {
    if (!block || block.getAttribute('data-pa-morse-preview-initialized') === 'true') return;
    block.setAttribute('data-pa-morse-preview-initialized', 'true');

    var root = block.closest('[data-pa-personalization-root]');
    var input = block.querySelector('[data-pa-morse-input]');
    var canvas = block.querySelector('[data-pa-morse-canvas]');
    var empty = block.querySelector('[data-pa-morse-empty]');
    var preview = block.querySelector('[data-pa-morse-preview]');
    var finishLabel = block.querySelector('[data-pa-morse-finish]');
    var characterCount = block.querySelector('[data-pa-morse-character-count]');
    var normalizationNote = block.querySelector('[data-pa-morse-normalization-note]');
    var pieceCount = block.querySelector('[data-pa-morse-piece-count]');
    var letterCodesContainer = block.querySelector('[data-pa-morse-letter-codes]');
    var continuousOutput = block.querySelector('[data-pa-morse-continuous]');
    var dotCountOutput = block.querySelector('[data-pa-morse-dot-count]');
    var dashCountOutput = block.querySelector('[data-pa-morse-dash-count]');
    var images = null;
    var dotImage;
    var dashImage;
    var redrawTimer = null;
    var observer = null;

    if (!input || !canvas) return;

    function render() {
      var rawValue = input.value || '';
      var normalized = normalizeName(rawValue);
      var letterCodes = Array.from(normalized).map(function (letter) {
        return { letter: letter, code: MORSE[letter] };
      });
      var sequence = letterCodes.map(function (item) { return item.code; }).join('');
      var dotCount = (sequence.match(/\./g) || []).length;
      var dashCount = sequence.length - dotCount;
      var finish = inferFinish(block);
      var compactSource = rawValue.toUpperCase().replace(/[\s'-]/g, '');
      var wasNormalized = !!normalized && compactSource !== normalized;

      drawPreview(canvas, sequence, finish, letterCodes, images);
      canvas.setAttribute(
        'aria-label',
        normalized
          ? 'Colar Morse com ' + normalized + ', ' + sequence.length + ' peças, acabamento ' + FINISHES[finish].label
          : 'Pré-visualização do Colar Morse sem texto'
      );

      if (empty) empty.hidden = !!normalized;
      if (preview) preview.hidden = !normalized;
      if (finishLabel) finishLabel.textContent = FINISHES[finish].label;
      if (characterCount) characterCount.textContent = normalized.length + '/10';
      if (pieceCount) pieceCount.textContent = sequence.length + (sequence.length === 1 ? ' peça' : ' peças');
      if (continuousOutput) continuousOutput.textContent = sequence || '—';
      if (dotCountOutput) dotCountOutput.textContent = dotCount;
      if (dashCountOutput) dashCountOutput.textContent = dashCount;

      if (normalizationNote) {
        normalizationNote.innerHTML = wasNormalized
          ? '<strong>Acentos ajustados.</strong> A produção irá usar <b>' + normalized + '</b>.'
          : normalized
            ? 'A produção irá usar <b>' + normalized + '</b>.'
            : 'Máx. 10 letras ou números. Acentos são ajustados automaticamente.';
        normalizationNote.classList.toggle('is-normalized', wasNormalized);
      }

      if (letterCodesContainer) {
        letterCodesContainer.innerHTML = '';
        letterCodes.forEach(function (item) {
          var cell = document.createElement('span');
          var letter = document.createElement('b');
          var code = document.createElement('code');
          letter.textContent = item.letter;
          code.textContent = item.code;
          cell.appendChild(letter);
          cell.appendChild(code);
          letterCodesContainer.appendChild(cell);
        });
      }
    }

    function scheduleRender() {
      window.clearTimeout(redrawTimer);
      redrawTimer = window.setTimeout(render, 80);
    }

    input.addEventListener('input', function () {
      render();
    });
    input.addEventListener('change', render);

    document.addEventListener('change', scheduleRender);
    ['variant:change', 'variantChange', 'product:variant-change'].forEach(function (eventName) {
      document.addEventListener(eventName, scheduleRender);
    });

    if (root && typeof MutationObserver === 'function') {
      observer = new MutationObserver(scheduleRender);
      observer.observe(root, {
        attributes: true,
        attributeFilter: ['data-product-full-title', 'data-product-variant-id']
      });
    }

    function imageReady() {
      if (
        dotImage && dotImage.complete && dotImage.naturalWidth &&
        dashImage && dashImage.complete && dashImage.naturalWidth
      ) {
        images = { dot: dotImage, dash: dashImage };
      }
      render();
    }

    dotImage = loadImage(block.getAttribute('data-pa-morse-image-dot'), imageReady);
    dashImage = loadImage(block.getAttribute('data-pa-morse-image-dash'), imageReady);
    render();

    block.paMorsePreviewDestroy = function () {
      window.clearTimeout(redrawTimer);
      if (observer) observer.disconnect();
      document.removeEventListener('change', scheduleRender);
      ['variant:change', 'variantChange', 'product:variant-change'].forEach(function (eventName) {
        document.removeEventListener(eventName, scheduleRender);
      });
    };
  }

  function initializeAll(scope) {
    var target = scope || document;
    if (target.matches && target.matches('[data-pa-morse-block]')) initializeBlock(target);
    Array.from(target.querySelectorAll ? target.querySelectorAll('[data-pa-morse-block]') : []).forEach(initializeBlock);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initializeAll(document); });
  } else {
    initializeAll(document);
  }

  document.addEventListener('shopify:section:load', function (event) {
    initializeAll(event.target);
  });

  document.addEventListener('shopify:section:unload', function (event) {
    var block = event.target.querySelector && event.target.querySelector('[data-pa-morse-block]');
    if (block && typeof block.paMorsePreviewDestroy === 'function') block.paMorsePreviewDestroy();
  });
})();
